import {Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {FormModel} from "../form/form.model.abstract";
import {InfoService} from "../services/info.service";
import {EmbedService} from "../services/embed.service";
import {BackendService} from "../services/backend.service";
import {AdvisorComponent} from "../advisor.component";
import {PortalService} from "../services/portal.service";

@Component({
  selector: '[app-info-icon]',
  templateUrl: './formfield.info.component.html'
})
export class FormfieldInfoComponent implements OnInit, OnChanges {
  /**
   * The configuration for this formfield
   */
  @Input() formModel: FormModel;

  /**
   * Extra Postfix for the headlinestring
   */
  @Input() headlinePostfix: string = null;

  /**
   * Fallbacktextkey for the headline if the original texkey does not exists.
   */
  @Input() headlineFallback: string = null;

  /**
   * Optional postfix for the fieldname to have some kind of fallback possibility for the images.
   *
   * @example if postfix = "|kalo" and fieldname is "energietraeger" than first "energietraeger|kalo" is searched and after that "energietraeger" as fallback
   */
  @Input() fieldNamePostfix: string = null;

  /**
   * Can receive an Eventemitter on which the info dialog should be shown.
   */
  @Input() openEvent: EventEmitter<boolean>;

  /**
   * Can receive an Eventemitter on which the info dialog should be closed
   */
  @Input() closeEvent: EventEmitter<void>;

  /**
   * optional extra Image that should be shown in the overlay.
   */
  @Input() extraImage: {path: string, marker: {top: number, left: number, width: number, height: number}[]} = null;

  /**
   * The canvas element for the (cropped) image
   */
  @ViewChild('canvasElement') canvasElement: ElementRef<HTMLCanvasElement>;

  /**
   * The html element with the infowindow
   */
  @ViewChild('infowindow') infowindow: ElementRef<HTMLElement>;

  /**
   * Holds the current advisor
   */
  advisor: string;

  /**
   * id of the html element
   */
  id: string;

  /**
   * calculated with of the visible overlay
   */
  width: number = 0;

  /**
   * toggle for showing/hinding teh dialog
   */
  showDialog: boolean = false;

  /**
   * Holds the imagedate if there is one from the backend.
   */
  imageData: {image: string, crop?: string, marker?: string[]} = null;

  /**
   * toggle for using the canvas element instead of an image.
   */
  useCanvas: boolean = false;

  /**
   * Toggle for showing the content as overlay.
   */
  showAsOverlay: boolean = false;

  /**
   * extra padding for the overlay to position in an embedded environment.
   */
  topPadding: number = 0;

  /**
   * negative margin on the top for infobuttons on the lower end of the page to avoind them stick out
   */
  topMargin: number = 0;

  /**
   * toggle for zooming the primary image
   */
  showZoomPrimary: boolean;

  /**
   * toggle for zooming the secondary image
   */
  showZoomSecondary: boolean;

  headerHeight: number;

  /**
   * constructor
   * @param element
   * @param backenService
   * @param infoService
   * @param embedService
   * @param portalService
   */
  constructor(private element: ElementRef, private backenService: BackendService, private infoService: InfoService, private embedService: EmbedService, private portalService: PortalService) {
    // get the current advisor
    this.advisor = this.backenService.getAdvisor();

    // subscribe to the event that is fired if any infowindow is opened.
    infoService.infoOpened.subscribe(id => {
      if(id !== this.formModel.id) {
        this.closeDialog();
      }
    });
  }

  /**
   * @inheritDoc
   */
  ngOnInit(): void {
    // set id and init the width
    this.id = this.formModel.id + '_info';

    // check if we want to show an image here
    this.initImage();

    // get header height for embedded cases
    this.headerHeight = this.portalService.getConfig('embed.stickyheader.mobile')? parseInt(this.portalService.getConfig('embed.stickyheader.mobile')) : 0;

    // reposition the image overlay on scrolling events if the tool is embeded
    if(this.embedService.isEmbedded()) {
      this.embedService.scrollEvent.subscribe(data => this.setPadding(data));
    }

    // if there is an eventemitter for showing the dialog, show it.
    if(this.openEvent) {
      this.openEvent.subscribe(overlay => {
        if(this.showDialog === false) {
          // open the dialog with a small delay to avoid rece conditions with the close handling in the service.
          setTimeout(() => {
            this.showAsOverlay = overlay;
            this.toggleDialog();
          }, 200);
        }
      });
    }

    // subscribe to the close event if there is one
    if(this.closeEvent) {
      this.closeEvent.subscribe(() => {
        this.infoService.infoOpened.emit(null);
      })
    }
  }

  /**
   * sets the top padding for embedded overlays
   * @param data
   */
  setPadding(data: {offset: number}): void {

    setTimeout(() => {
      const parentFieldset = this.element.nativeElement.closest(".formfield");
      const boundings = parentFieldset?.getBoundingClientRect()?? {x: 0};
      this.topPadding = (data.offset < 0? data.offset : 0) * -1 + this.headerHeight + boundings.x;
    }, 0);
  }

  /**
   * toggle (open/close) the info dialog
   */
  toggleDialog(): void {
    if(this.showDialog === true) {
      this.closeDialog();
    }
    else {
      this.showDialog = true;
      this.infoService.openInfoWindow(this.formModel.id);
      this.checkTopPosition();
      this.setWidth();
      if(window.screen.width < AdvisorComponent.SCREEN_WIDTH_MEDIUM) {
        this.showAsOverlay = true;
      }
      if(this.showAsOverlay === true && this.embedService.isEmbedded() === true) {
        this.setPadding({offset: this.embedService.currentOffset})
      }
    }
  }

  /**
   * Close the info dialog
   */
  closeDialog(): void {
    this.showDialog = false;
    this.showZoomPrimary = false;
    this.showZoomSecondary = false;
    this.showAsOverlay = false;
    this.infoService.closeInfoWindow(this.formModel.id);
  }

  /**
   * Listen for global key events.
   * @param event
   */
  @HostListener('window:keydown', ['$event'])
  onGlobalKeyDown(event: any): void {
    switch(event.which) {
      case 27:
        // ESC
        if(this.showAsOverlay === true) {
          this.showAsOverlay = false;
        }
        else if(this.showDialog === true) {
          this.closeDialog();
        }
        break;
    }
  }

  /**
   * Eventhalnder for keypresses to show/hide the infobutton
   * @param event
   */
  onKeyDown(event: any): void {
    switch(event.which) {
      case 13:
        // ENTER
        this.toggleDialog();
        break;
    }
  }

  /**
   * Sets the available width of the infobutton content
   */
  setWidth(): void {
    if(this.element.nativeElement.closest) {
      const parentFieldset = this.element.nativeElement.closest(".formfield");
      if(parentFieldset) {
        this.width = parentFieldset.parentElement.getBoundingClientRect().width - parentFieldset.getBoundingClientRect().width;
      }
    }
  }

  /**
   * checks the top position of the element and if it is visible. completly. If not, move it more to the top.
   */
  checkTopPosition(): void {
    this.topMargin = 0;
    setTimeout(() => {
      const chapter = document.querySelector('.step-content-wrapper');
      const element = this.infowindow.nativeElement.getBoundingClientRect();
      const maxBottom = chapter? chapter.getBoundingClientRect().bottom : (window.innerHeight || document.documentElement.clientHeight);
      if(element.bottom > maxBottom && window.screen.width > AdvisorComponent.SCREEN_WIDTH_MEDIUM) {
        this.topMargin = maxBottom - element.bottom;
      }
    }, 100)
  }

  /**
   * Initialize the image wich we maybe have. Ask the backend for image data.
   * If there is none, nothing is shown.
   * We have a fallback here. It's searched for [fieldid + fieldNamePostfix, fieldid] so it's possible to have some kind of fallbacks here.
   */
  initImage(): void {
    let fields = [this.formModel.id];
    if(this.headlinePostfix) {
      fields.push(this.formModel.id + this.headlinePostfix.replace("|", "_"));
    }
    this.infoService.getInfobuttonImage(this.advisor, fields).subscribe(result => {
      if(result != null && result.hasOwnProperty("image") && result.image !== null) {
        this.imageData = result;
        if(result.hasOwnProperty("crop") && result["crop"] && result["crop"].length > 0) {
          this.useCanvas = true;
          // show image as cropped canvas element
          let image = new Image();
          image.onload = () => {
            if(!this.canvasElement) {
              return;
            }

            // x1,y1,x2,y2
            const coordinates = result["crop"].split(",").map(element => parseInt(element));

            const sourceX = coordinates[0];
            const sourceY = coordinates[1];
            const sourceWidth = coordinates[2] - coordinates[0];
            const sourceHeight = coordinates[3] - coordinates[1];
            const destWidth = sourceWidth;
            const destHeight = sourceHeight;
            this.canvasElement.nativeElement.width = destWidth;
            this.canvasElement.nativeElement.height = destHeight;

            // draw the cropped image
            let context = this.canvasElement.nativeElement.getContext("2d");
            context.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, destWidth, destHeight);

            // draw marker
            if(result.hasOwnProperty("marker") && result["marker"].length > 0) {
              context.fillStyle = InfoService.INFOBUTTON_MARKER_FILL;
              context.strokeStyle = InfoService.INFOBUTTON_MARKER_BORDER;
              for(let marker of result["marker"]) {
                const coordinates = marker.split(",").map(element => parseInt(element));
                context.fillRect(coordinates[0] - sourceX,coordinates[1] - sourceY,coordinates[2] - coordinates[0],coordinates[3] - coordinates[1]);
                context.strokeRect(coordinates[0] - sourceX,coordinates[1] - sourceY,coordinates[2] - coordinates[0],coordinates[3] - coordinates[1]);
              }
            }
          };
          image.src = result["image"];
        }
      }
      else {
        // reset image date in case of the msp has changed
        this.imageData = null;
      }
    });
  }

  /**
   * toggles the overlay mode
   */
  toggleOverlay(event: any): void {
    if(this.showAsOverlay === false || (event.target.classList.contains('image-wrapper') || event.target.classList.contains('close'))) {
      this.showAsOverlay = !this.showAsOverlay;
      if (this.showAsOverlay === false) {
        this.closeDialog();
      }
      else if(this.showAsOverlay === true && this.embedService.isEmbedded()) {
        this.setPadding({offset: this.embedService.currentOffset});
      }
    }
  }

  /**
   * show bigger size of the primary image
   */
  zoomPrimary(): void {
    if(this.extraImage && this.showAsOverlay && window.screen.width >= AdvisorComponent.SCREEN_WIDTH_MEDIUM) {
      this.showZoomPrimary = !this.showZoomPrimary;
    }
  }

  /**
   * show bigger size of the secondary image
   */
  zoomSecondary(): void {
    if(this.showAsOverlay && window.screen.width >= AdvisorComponent.SCREEN_WIDTH_MEDIUM) {
      this.showZoomSecondary = !this.showZoomSecondary;
    }
  }

  /**
   * @inheritDoc
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    if(changes.hasOwnProperty('fieldNamePostfix') && changes['fieldNamePostfix'].isFirstChange() === false) {
      // image must be updated if the postfix is changed.
      this.initImage();
    }
  }

}
